import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { Lock, Send, CheckCircle } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import { formatListName } from "../../utils/listName";

/**
 * AccessRequestModal
 * Se muestra cuando un vendedor intenta crear un lead con teléfono ya existente (409)
 * pero no tiene acceso a ese lead. Permite solicitar acceso al admin.
 */
export default function AccessRequestModal({
  open,
  onClose,
  contactId,
  contactName,
  contactPhone,
  contactLists,
  onRequested,
}) {
  const [status, setStatus] = useState("idle"); // "idle" | "sending" | "sent" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  if (!open) return null;

  const listLabel = Array.isArray(contactLists) && contactLists.length > 0
    ? contactLists.map(formatListName).join(", ")
    : "lista desconocida";

  const handleRequest = async () => {
    setStatus("sending");
    setErrorMsg("");
    try {
      await axiosClient.post("/ventas/access-requests", {
        contact_id: contactId,
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_lists: contactLists,
      });
      setStatus("sent");
      onRequested?.();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || err?.message || "Error al enviar la solicitud");
      setStatus("error");
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-br from-slate-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 rounded-2xl">
              <Lock size={18} className="text-amber-500" />
            </div>
            <div>
              <h2 className="text-base font-black text-slate-900">Lead ya registrado</h2>
              <p className="text-xs text-slate-400 font-medium">Sin acceso asignado</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-all"
          >
            <FaTimes className="text-slate-400" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-4">
          {status !== "sent" ? (
            <>
              <p className="text-sm text-slate-600 leading-relaxed">
                El número <span className="font-bold text-slate-800">{contactPhone}</span> ya está
                registrado como <span className="font-bold text-slate-800">{contactName || "lead existente"}</span>{" "}
                en la lista <span className="font-bold text-red-600">{listLabel}</span>, pero no tienes acceso a ese lead.
              </p>
              <p className="text-xs text-slate-400">
                Puedes solicitar acceso al administrador. Recibirás el lead en tu lista cuando lo apruebe.
              </p>

              {status === "error" && (
                <p className="text-xs text-red-500 font-medium bg-red-50 px-3 py-2 rounded-xl border border-red-100">
                  {errorMsg}
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleRequest}
                  disabled={status === "sending"}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md shadow-red-100"
                >
                  <Send size={14} />
                  {status === "sending" ? "Enviando..." : "Solicitar acceso"}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4 space-y-3">
              <div className="flex justify-center">
                <div className="p-4 bg-emerald-50 rounded-full">
                  <CheckCircle size={32} className="text-emerald-500" />
                </div>
              </div>
              <h3 className="font-black text-slate-900">Solicitud enviada</h3>
              <p className="text-sm text-slate-500">
                El administrador revisará tu solicitud. El lead aparecerá en tu lista en cuanto sea aprobado.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 px-6 py-2.5 rounded-2xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition-all"
              >
                Entendido
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
